import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Navigate } from "react-router-dom";
import { scheduleBatch, resetSuccess } from "./BatchScheduler.action";
import Container from "react-bootstrap/Container";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FormLabel from "react-bootstrap/FormLabel";
import FormGroup from "react-bootstrap/FormGroup";
import FormControl from "react-bootstrap/FormControl";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Alert from "react-bootstrap/Alert";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { DateRangePicker } from "rsuite";
import ReactBadge from "react-bootstrap/Badge";
import { format } from "date-fns";
import Select from "react-select";
import { Form } from "react-bootstrap";
import { showNotification } from "services/NotificationService";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import MessageBanner from "../../../components/MessageBanner";
import { isLoggedInUserBioSimilarUser } from "services/AWUtilityService";

const Tip = (props) => (
  <div style={{ textAlign: "right", color: "#000", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const customStyles = {
  multiValue: (base) => ({
    background: "#007BBB",
    display: "inline-flex",
    color: "white",
    borderRadius: "11px",
    alignItems: "center",
  }),
  multiValueLabel: (base) => ({
    fontSize: "13px",
    color: "white",
    padding: "2px 4px 2px 4px",
  }),
};

const BatchScheduler = () => {
  const [description, setDescription] = useState("");
  const [dateTime, setDateTime] = useState(new Date());
  const [switchDateRange, setSwitchDateRange] = useState(null);
  const [createAllActivities, setCreateAllActivities] = useState(true);
  const [error, setError] = useState("");
  const [noOfSelectedSwitches, setNoOfSelectedSwitches] = useState(0);
  const [activityCount, setActivityCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({
    clientNameAbbreviation: [],
    customerNameAbbreviation: [],
    populationType: null,
  });
  const [filterSelectMap, setFilterSelectMap] = useState({});
  const [originalFilterState, setOriginalFilterState] = useState({});
  const [isShowBioSimilarToggle, setIsShowBioSimilarToggle] = useState(false);
  const [clientDetailsMap, setClientDetailsMap] = useState({});

  const batchSchedulerState = useSelector((state) => state.batchScheduler);
  const {
    error: reduxError,
    submittingRequest,
    isSuccess,
    disableAction,
    eventRequestCompleted,
  } = batchSchedulerState;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const addClientsToCustomers = useCallback(
    (
      customerListArray = selectedFilters.customerNameAbbreviation,
      clientListArray = selectedFilters.clientNameAbbreviation,
      populationTypeOverride = null
    ) => {
      let extractedSelectedClients = clientListArray;

      if (clientListArray.length === 0) {
        const appliedCustomerFilters = customerListArray.map(
          (customer) => customer.label
        );
        const extractedSelectedCustomers = filterSelectMap[
          "customerClientMapping"
        ].filter((e) => appliedCustomerFilters.includes(e.value));
        const extractedClients = extractedSelectedCustomers.reduce(
          (result, entry) => {
            return result.concat(entry.label);
          },
          []
        );

        let clientsForCustomer = originalFilterState.clientNameAbbreviation.filter(
          (item) => new Set(extractedClients).has(item.label)
        );

        // Use override if provided, otherwise use selectedFilters.populationType
        const populationType =
          populationTypeOverride !== null
            ? populationTypeOverride
            : selectedFilters.populationType;

        // When populationType is null (nothing selected), include all clients for the customer
        // When populationType is set, filter by that population type
        if (populationType) {
          const populationTypeValue =
            typeof populationType === "string"
              ? populationType
              : populationType.value;
          clientsForCustomer = clientsForCustomer.filter((clientOption) => {
            const clientDetail = clientDetailsMap[clientOption.label];
            return (
              clientDetail &&
              clientDetail.populationType === populationTypeValue
            );
          });
        }
        // When populationType is null, clientsForCustomer already contains all clients for the customer
        extractedSelectedClients = clientsForCustomer;
      }
      // When clients are explicitly selected, use them as-is (don't filter by populationType)

      return extractedSelectedClients.map((client) => client.value);
    },
    [
      selectedFilters.customerNameAbbreviation,
      selectedFilters.clientNameAbbreviation,
      selectedFilters.populationType,
      filterSelectMap,
      originalFilterState,
      clientDetailsMap,
    ]
  );

  const fetchNoofSelectedSwitches = useCallback(
    (clients, startDate, endDate, isShowBioSimilar, populationType) => {
      let url = `/swim/get-selected-switches-count?date_scheduled=${
        dateTime
          ? format(dateTime, "yyyy-MM-dd HH:mm:ss")
          : format(new Date(), "yyyy-MM-dd HH:mm:ss")
      }`;
      url = url.concat(
        clients && !clients.includes("00000000-0000-0000-0000-000000000000")
          ? `&client_list=${clients}`
          : ""
      );
      url = url.concat(
        startDate
          ? `&start_date=${format(
              startDate,
              "yyyy-MM-dd HH:mm:ss"
            )}&end_date=${format(endDate, "yyyy-MM-dd HH:mm:ss")}`
          : ""
      );
      url = url.concat(`&show_bio_similar=${isShowBioSimilar}`);
      url = url.concat(
        populationType ? `&population_type=${populationType}` : ""
      );
      sendAuthenticatedAsyncRequest(url, "GET", null, (r) =>
        setNoOfSelectedSwitches(r.data)
      );
    },
    [dateTime]
  );

  useEffect(() => {
    if (isSuccess) {
      showNotification({
        title: "Batch Created!",
        message: "Batch is scheduled successfully.",
        position: "bl",
        type: "success",
      });
      dispatch(resetSuccess());
      navigate("/scheduler/batch/list");
    }
  }, [isSuccess, dispatch, navigate]);

  const updateFilters = (tempFilters) => {
    let tempFilterSelectMap = {};
    let tempClientDetailsMap = {};

    Object.keys(tempFilters).forEach((colKey) => {
      if (colKey === "clientNameAbbreviation") {
        tempFilters[colKey].forEach((client) => {
          tempClientDetailsMap[client.value] = client;
        });
      }
      tempFilterSelectMap[colKey] = tempFilters[colKey].map((row) => ({
        value: row.id,
        label: row.value,
      }));
    });

    setFilterSelectMap(tempFilterSelectMap);
    setOriginalFilterState({ ...tempFilterSelectMap });
    setClientDetailsMap(tempClientDetailsMap);
  };

  useEffect(() => {
    sendAuthenticatedAsyncRequest(`/swim/open-batch-exists`, "GET", null, (r) =>
      dispatch({ type: "SET_DISABLE_ACTION", payload: r.data })
    );

    sendAuthenticatedAsyncRequest(
      `/swim/batch/get-client-customer-filters`,
      "GET",
      null,
      (r) => {
        if (r.data.filters !== null) {
          updateFilters(r.data.filters);
        }
      },
      (r) => {
        showNotification({
          title: "Error fetching Filter Values!",
          message: "Something went Wrong.",
          position: "tr",
          type: "error",
        });
      }
    );
  }, [dispatch]);

  const handleChange = (e) => {
    const { value } = e.target;
    setDescription(value);
    setError("");
  };

  const handleCreateAllActivitiesChange = (e) => {
    const { checked } = e.target;
    setCreateAllActivities(checked);
    if (checked) {
      setActivityCount(0);
    }
  };

  const validateForm = (dateTime, clientList) => {
    var currentDate = new Date();
    if (!dateTime) return "You need to select Date & Time";
    if (dateTime < currentDate)
      return "Date and Time should be greater then current Time";
    if (!clientList || clientList.length < 1)
      return "You need to select at least one client";
    return null;
  };

  const scheduleBatchHandler = (e) => {
    const tempClientList = addClientsToCustomers();
    var validationMessage = validateForm(dateTime, tempClientList);
    if (validationMessage === null) {
      const batchDto = {
        scheduledDate: dateTime,
        description: description,
        clientList: tempClientList,
        switchCreationDateRange: Array.isArray(switchDateRange)
          ? switchDateRange
          : null,
        createAllActivities: createAllActivities,
        activityCount: activityCount,
      };
      dispatch(scheduleBatch(batchDto));
    } else {
      setError(validationMessage);
    }
  };

  const onDateChange = (newDateTime) => {
    setDateTime(newDateTime);
    setError("");
    fetchNoofSelectedSwitches(
      addClientsToCustomers(),
      switchDateRange ? switchDateRange[0] : null,
      switchDateRange ? switchDateRange[1] : null,
      isShowBioSimilarToggle,
      selectedFilters.populationType
        ? selectedFilters.populationType.value
        : null
    );
  };

  const onDateRangeChange = (newSwitchDateRange) => {
    setSwitchDateRange(newSwitchDateRange);
    setError("");
    fetchNoofSelectedSwitches(
      addClientsToCustomers(),
      newSwitchDateRange ? newSwitchDateRange[0] : null,
      newSwitchDateRange ? newSwitchDateRange[1] : null,
      isShowBioSimilarToggle,
      selectedFilters.populationType
        ? selectedFilters.populationType.value
        : null
    );
  };

  const handleToggleChangeForBioSimilar = (e) => {
    const isChecked = e.target.checked;
    setIsShowBioSimilarToggle(isChecked);
    fetchNoofSelectedSwitches(
      addClientsToCustomers(),
      switchDateRange ? switchDateRange[0] : null,
      switchDateRange ? switchDateRange[1] : null,
      isChecked,
      selectedFilters.populationType
        ? selectedFilters.populationType.value
        : null
    );
  };

  const handleCustomerChange = (option) => {
    const updatedCustomerList = option ? [option] : [];
    let filteredClientOptions =
      originalFilterState.clientNameAbbreviation || [];
    let populationTypeOptions = [];

    if (option) {
      const customerToClientsMap =
        filterSelectMap["customerClientMapping"] || [];
      const mapping = customerToClientsMap.find(
        (m) => m.value === option.label
      );
      const clientLabelsForCustomer = mapping ? mapping.label : [];

      filteredClientOptions = originalFilterState.clientNameAbbreviation.filter(
        (client) => clientLabelsForCustomer.includes(client.label)
      );

      const uniquePopulationTypes = new Set();
      filteredClientOptions.forEach((clientOption) => {
        const clientDetail = clientDetailsMap[clientOption.label];
        if (clientDetail && clientDetail.populationType) {
          uniquePopulationTypes.add(clientDetail.populationType);
        }
      });

      populationTypeOptions = Array.from(uniquePopulationTypes).map((pt) => ({
        value: pt,
        label: pt,
      }));
    }

    setSelectedFilters({
      customerNameAbbreviation: updatedCustomerList,
      populationType: null,
      clientNameAbbreviation: [],
    });

    setFilterSelectMap((prevState) => ({
      ...prevState,
      clientNameAbbreviation: filteredClientOptions,
      populationType: populationTypeOptions,
    }));

    setNoOfSelectedSwitches(0);

    const tempClientList = addClientsToCustomers(updatedCustomerList, []);
    if (
      !(
        updatedCustomerList &&
        updatedCustomerList.length === 1 &&
        updatedCustomerList[0].label === "WMT"
      )
    ) {
      if (isShowBioSimilarToggle) {
        setIsShowBioSimilarToggle(!isShowBioSimilarToggle);
      }
    }

    fetchNoofSelectedSwitches(
      tempClientList,
      switchDateRange ? switchDateRange[0] : null,
      switchDateRange ? switchDateRange[1] : null,
      isShowBioSimilarToggle,
      null
    );
  };

  const handlePopulationTypeChange = (option) => {
    const selectedCustomer = selectedFilters.customerNameAbbreviation[0];
    let clientOptionsForCustomer = [];

    if (selectedCustomer) {
      const customerToClientsMap =
        filterSelectMap["customerClientMapping"] || [];
      const mapping = customerToClientsMap.find(
        (m) => m.value === selectedCustomer.label
      );
      const clientLabelsForCustomer = mapping ? mapping.label : [];
      clientOptionsForCustomer = originalFilterState.clientNameAbbreviation.filter(
        (client) => clientLabelsForCustomer.includes(client.label)
      );
    } else {
      clientOptionsForCustomer =
        originalFilterState.clientNameAbbreviation || [];
    }

    let finalClientOptions = clientOptionsForCustomer;

    if (option) {
      const selectedPopulationType = option.value;
      finalClientOptions = clientOptionsForCustomer.filter((clientOption) => {
        const clientDetail = clientDetailsMap[clientOption.label];
        return (
          clientDetail && clientDetail.populationType === selectedPopulationType
        );
      });
    }

    setSelectedFilters((prevState) => ({
      ...prevState,
      populationType: option,
      clientNameAbbreviation: [],
    }));

    setFilterSelectMap((prevState) => ({
      ...prevState,
      clientNameAbbreviation: finalClientOptions,
    }));

    setNoOfSelectedSwitches(0);

    const tempClientList = addClientsToCustomers(
      selectedFilters.customerNameAbbreviation,
      [],
      option // Pass the population type option directly
    );

    fetchNoofSelectedSwitches(
      tempClientList,
      switchDateRange ? switchDateRange[0] : null,
      switchDateRange ? switchDateRange[1] : null,
      isShowBioSimilarToggle,
      option ? option.value : null
    );
  };

  const handleClientChange = (option) => {
    let clientListArray = option !== null ? option : [];
    setSelectedFilters((prevState) => ({
      ...prevState,
      clientNameAbbreviation: clientListArray,
    }));

    const tempClientList = addClientsToCustomers(
      selectedFilters.customerNameAbbreviation,
      clientListArray
    );

    fetchNoofSelectedSwitches(
      tempClientList,
      switchDateRange ? switchDateRange[0] : null,
      switchDateRange ? switchDateRange[1] : null,
      isShowBioSimilarToggle,
      selectedFilters.populationType
        ? selectedFilters.populationType.value
        : null
    );
  };

  const handleActivityCountChange = (e) => {
    setCreateAllActivities(!e.target.value);
  };

  const handleNumberInputChange = (e) => {
    setActivityCount(e.target.value);
  };

  if (eventRequestCompleted) return <Navigate to={`/scheduler/batch/list`} replace />;

  return (
    <Container fluid className="top-spacer">
      <Row>
        <Col md={{ span: 15 }}>
          <MessageBanner
            message={
              "You cannot schedule a new Batch as one is already being processed"
            }
            isDisplayMessage={disableAction}
            icon={faExclamationCircle}
          />
          <div>
            <Card variant="primary">
              <Card.Header>
                <Card.Title className="text-center">
                  <h4>Scheduler</h4>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {error ? <Alert variant="danger">{error}</Alert> : null}
                {reduxError ? (
                  <Alert variant="danger">{reduxError}</Alert>
                ) : null}
                {submittingRequest ? (
                  <Alert variant="primary">{submittingRequest}</Alert>
                ) : null}
                <Row>
                  <Col md={12}>
                    <Tip>Enter Date & Time according to your Time Zone</Tip>
                  </Col>
                  <Col md={12}>
                    <FormGroup as={Row}>
                      <Col md={3}>
                        <FormLabel>Date & Time</FormLabel>
                      </Col>
                      <Col md={9}>
                        <DateTimePicker
                          onChange={onDateChange}
                          value={dateTime}
                          disabled={disableAction}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup className="tiny-top-spacer" as={Row}>
                      <Col md={3}>
                        <FormLabel>Description </FormLabel>
                      </Col>
                      <Col md={9}>
                        <FormControl
                          type="text"
                          name="description"
                          value={description}
                          placeholder="Enter description (Optional)"
                          onChange={handleChange}
                          disabled={disableAction}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup as={Row}>
                      <Col md={3}>
                        <FormLabel>Customer </FormLabel>
                      </Col>
                      <Col md={9}>
                        <Select
                          options={
                            Object.keys(filterSelectMap).includes(
                              "customerNameAbbreviation"
                            )
                              ? filterSelectMap["customerNameAbbreviation"]
                              : []
                          }
                          value={selectedFilters.customerNameAbbreviation}
                          onChange={handleCustomerChange}
                          placeholder={
                            selectedFilters.clientNameAbbreviation.length === 0
                              ? "Enter Customer (Mandatory)"
                              : "Enter Customer (Optional)"
                          }
                          isMulti={false}
                          isClearable
                          isDisabled={disableAction}
                          styles={customStyles}
                        ></Select>
                      </Col>
                    </FormGroup>
                    <FormGroup className="tiny-top-spacer" as={Row}>
                      <Col md={3}>
                        <FormLabel>Population Type</FormLabel>
                      </Col>
                      <Col md={9}>
                        <Select
                          options={filterSelectMap.populationType || []}
                          value={selectedFilters.populationType}
                          onChange={handlePopulationTypeChange}
                          placeholder="Select Population Type (Optional)"
                          isClearable
                          isDisabled={
                            disableAction ||
                            selectedFilters.customerNameAbbreviation.length ===
                              0
                          }
                          styles={customStyles}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup className="tiny-top-spacer" as={Row}>
                      <Col md={3}>
                        <FormLabel>Client </FormLabel>
                      </Col>
                      <Col md={9}>
                        <Select
                          options={
                            Object.keys(filterSelectMap).includes(
                              "clientNameAbbreviation"
                            )
                              ? filterSelectMap["clientNameAbbreviation"]
                              : []
                          }
                          onChange={handleClientChange}
                          value={selectedFilters.clientNameAbbreviation}
                          placeholder={
                            selectedFilters.customerNameAbbreviation.length ===
                            0
                              ? "Enter Client (Mandatory)"
                              : "Enter Client (Optional)"
                          }
                          isMulti
                          isDisabled={disableAction}
                          styles={customStyles}
                          closeMenuOnSelect={false}
                        ></Select>
                      </Col>
                    </FormGroup>
                    <FormGroup className="tiny-top-spacer" as={Row}>
                      <Col md={3}>
                        <FormLabel>Switch Creation Date Range </FormLabel>
                      </Col>
                      <Col md={9}>
                        <DateRangePicker
                          block
                          style={{ position: "relative", zIndex: "0" }}
                          name="switchCreationDateRange"
                          value={switchDateRange}
                          onChange={onDateRangeChange}
                          format="yyyy-MM-dd"
                          placeholder="Enter Date Range (Optional)"
                          disabled={disableAction}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup className="tiny-top-spacer" as={Row}>
                      <Col md={3}>
                        <FormLabel>No. of Selected Switches </FormLabel>
                      </Col>
                      <Col md={9}>
                        <ReactBadge
                          id="no-of-selected-switches"
                          variant="success"
                          pill
                          style={{ margin: "-1px 5px", fontSize: "100%" }}
                        >
                          {noOfSelectedSwitches ? noOfSelectedSwitches : 0}
                        </ReactBadge>
                      </Col>
                    </FormGroup>
                    <FormGroup as={Row} className="align-items-center">
                      <Col md={12}>
                        <Row className="align-items-center">
                          <Col md="auto">
                            <Form.Check
                              type="radio"
                              name="activityFiltering"
                              id="batch-scheduler-create-all-activities"
                              label="Create All Activities"
                              onChange={handleCreateAllActivitiesChange}
                              style={{ zIndex: "0" }}
                              defaultChecked={true}
                              disabled={disableAction}
                              className="mr-3"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md="auto">
                            <Form.Check
                              type="radio"
                              name="activityFiltering"
                              id="batch-scheduler-activity-count"
                              label="Activity Count:"
                              onChange={handleActivityCountChange}
                              style={{ zIndex: "0" }}
                              disabled={disableAction}
                              className="mr-2"
                            />
                          </Col>
                          <Col md="auto">
                            <Form.Control
                              type="number"
                              value={activityCount}
                              onChange={handleNumberInputChange}
                              disabled={createAllActivities}
                              style={{ width: "80px" }}
                              className="mr-3"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md="auto">
                            {(selectedFilters.clientNameAbbreviation.some(
                              (client) => client.label === "WMT"
                            ) ||
                              selectedFilters.customerNameAbbreviation.some(
                                (customer) => customer.label === "WMT"
                              )) &&
                              isLoggedInUserBioSimilarUser() && (
                                <Form.Check
                                  type="switch"
                                  id="bioSimilarSwitchesToggle"
                                  label="Bio Similar Switches"
                                  onChange={handleToggleChangeForBioSimilar}
                                  checked={isShowBioSimilarToggle}
                                  className="mr-3"
                                />
                              )}
                          </Col>
                        </Row>
                      </Col>
                      <Col md={12} className="d-flex justify-content-end mt-3">
                        <Button
                          variant="secondary"
                          style={{
                            paddingLeft: "50px",
                            paddingRight: "50px",
                          }}
                          disabled={disableAction || noOfSelectedSwitches === 0}
                          onClick={scheduleBatchHandler}
                        >
                          Schedule Batch
                        </Button>
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BatchScheduler;
